import React, { ReactNode, useEffect, useReducer, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce/lib";

import { getApplication } from "../../api/application";
import { calculatePremium, submitDlpState, updateDlpState } from "../../api/dlp";
import { fetchDictionary } from "../../api/genericData";
import { LoadingOverlay } from "../../components/loaders/LoadingOverlay";
import { PORTALTHIRD_BASE_URL } from "../../constants/constants";
import { DlpFlowContext } from "../../context/DlpFlowContext";
import { useDidMount } from "../../hooks/useDidMount";
import { useQueryParams } from "../../hooks/useQueryParams";
import { YesOrNo } from "../../interfaces/enum";
import { ModelState } from "../../interfaces/modelState";
import { PaymentTerm } from "../../interfaces/paymentTerm";
import { convertDlpStateToCalculationModel, convertDlpStateToSaveDlpStateRequest } from "./DlpFlow.converters";
import { isCoveragesComplete } from "./DlpFlow.coverages";
import { isInsuredComplete } from "./DlpFlow.insured";
import {
  AcceptApplicationData,
  CoverageInsuranceType,
  DlpContext,
  DlpState,
  InsuredPerson,
  OptionsStepModel,
  PremiumTerms,
} from "./DlpFlow.interfaces";
import { dlpFlowStateReducer, initialState } from "./DlpFlowState";

const QUERY_PARAM_APPLICATION_ID = "applicationId";

interface Props {
  children: ReactNode;
}

export const DlpFlowProvider = ({ children }: Props) => {
  const history = useHistory();
  const queryParams = useQueryParams();
  const applicationIdRef = useRef(queryParams.get(QUERY_PARAM_APPLICATION_ID));

  const [dictionary, setDictionary] = useState<Record<string, string>>({});
  const [isLoadingExistingApplication, setIsLoadingExistingApplication] = useState(false);
  const [isLoadingPremium, setLoadingPremium] = useState(false);
  const [premium, setPremium] = useState<PremiumTerms | null>(null);
  const [modelState, setModelState] = useState<ModelState | null>(null);
  const [data, dispatch] = useReducer(dlpFlowStateReducer, initialState);
  const [iban, setIban] = useState<string | undefined>(undefined);
  const [acceptApplication, setAcceptApplication] = useState<AcceptApplicationData | undefined>(undefined);
  const currentStepRef = useRef(1);
  const shouldRetrievePremiumsRef = useRef(false);

  const retrievePremium = useDebouncedCallback(() => {
    const canCalculatePremium = data.insuredPersons.every(isInsuredComplete) && isCoveragesComplete(data.coverages);
    if (canCalculatePremium) {
      setLoadingPremium(true);

      const request = {
        ...convertDlpStateToCalculationModel(data),
      };

      calculatePremium(request)
        .then((response) => {
          if ("modelState" in response) {
            updateModelState(response.modelState);
          } else {
            if (
              response.totalPremiumPerTerms.length !== premium?.totalPremiumPerTerms.length ||
              premium?.totalPremiumPerTerms?.length === undefined
            ) {
              dispatch({
                type: "UPDATE_PAYMENT_TERM",
                payload: { paymentTerm: response.totalPremiumPerTerms[0].paymentTerm },
              });
            }
            setPremium(response);
            setModelState(null);
          }
        })
        .catch((e) => console.error(`Failed to load premiums information: ${e}`))
        .finally(() => {
          setLoadingPremium(false);
          shouldRetrievePremiumsRef.current = false;
        });
    }
  }, 500);

  const goBack = (stepNumber: number, path: string) => {
    currentStepRef.current = stepNumber;
    navigateToPath(path);
  };

  const retrieveApplication = async () => {
    if (!applicationIdRef.current) {
      return;
    }
    setIsLoadingExistingApplication(true);
    try {
      const insuranceData = await getApplication(applicationIdRef.current);
      const parsedInsuranceData = JSON.parse(insuranceData.json) as DlpState;

      if (parsedInsuranceData.coverages && parsedInsuranceData.coverages.length > 0) {
        parsedInsuranceData.coverages = parsedInsuranceData.coverages.map((c) => {
          if (c.insuredAmount) {
            c.hasInsuredAmount = true;
          }

          if (c.duration) {
            c.hasDuration = true;
          }

          if (c.effectiveDate) {
            c.hasEffectiveDate = true;
          }

          return c;
        });
      }

      shouldRetrievePremiumsRef.current = true;

      dispatch({ type: "REHYDRATE", payload: { data: parsedInsuranceData } });
    } catch (e) {
      console.error(`Failed to retrieve application: ${e}`);
    } finally {
      setIsLoadingExistingApplication(false);
    }
  };

  const saveDlpState = async () => {
    const requestData = convertDlpStateToSaveDlpStateRequest(data, premium, applicationIdRef.current);

    const response = await updateDlpState(requestData);

    if (!response.applicationId) {
      throw Error("NO_APPLICATION_ID_RETURNED_FROM_SAVE_DLP_STATE");
    }

    if (response.applicationId !== applicationIdRef.current) {
      applicationIdRef.current = response.applicationId;
    }
  };

  const navigateToPath = (path: string) => {
    const url = new URL(window.location.href);
    const search = new URLSearchParams(url.searchParams);

    if (applicationIdRef.current) {
      search.set(QUERY_PARAM_APPLICATION_ID, applicationIdRef.current);
    } else {
      search.delete(QUERY_PARAM_APPLICATION_ID);
    }

    window.scrollTo(0, 0);
    history.push({
      pathname: `/${PORTALTHIRD_BASE_URL}/${path}`,
      search: search.toString(),
    });
  };

  const submitStep = async (requestData: object, url: string) => {
    const result = await submitDlpState(url, {
      ...requestData,
      applicationId: applicationIdRef.current,
    });

    if (result?.modelState) {
      setModelState(result.modelState);
      throw new Error("SUBMIT_DLP_STATE_VALIDATION_FAILED");
    }
  };

  const goToStepTwo = async (path: string) => {
    await saveDlpState();

    const calculationStep = convertDlpStateToCalculationModel(data);
    await submitStep(calculationStep, "dlpflow/submitCalculate");

    currentStepRef.current = 2;

    navigateToPath(path);
  };

  const goToStepThree = async (path: string) => {
    await saveDlpState();

    const optionsStepModel: OptionsStepModel = {
      crosswiseInsured: data.crosswiseInsured,
      hasMortgage: data.hasMortgage,
      insuranceTaker: data.insuranceTaker,
      mortgageSupplierKey: data.mortgageSupplierKey,
      mortgageSupplierLabel: data.mortgageSupplierLabel,
      hasSecondInsured: data.insuredPersons.length === 2 ? "Yes" : "No",
    };
    await submitStep(optionsStepModel, "dlpflow/submitOptions");

    currentStepRef.current = 3;

    navigateToPath(path);
  };

  const goToStepFour = async (path: string) => {
    await saveDlpState();

    await submitStep({ insuredPersons: data.insuredPersons }, "dlpflow/submitPersonalDetails");

    currentStepRef.current = 4;

    navigateToPath(path);
  };

  const goToStepFive = (path: string) => {
    currentStepRef.current = 5;

    navigateToPath(path);
  };

  const submitOffer = async (url: string) => {
    const totalPremiumPerTerms = { premium: { terms: premium?.totalPremiumPerTerms } };
    const request = {
      applicationId: applicationIdRef.current,
      dlpApplication: { ...data, ...totalPremiumPerTerms, iban, ...acceptApplication },
    };

    await submitStep(request, "dlpflow/submitOffer");

    // remove the query parameters
    window.history.replaceState(null, "", window.location.pathname);
    history.push(url);
  };

  const updateIbanNumber = (value: string) => {
    setIban(value);
  };

  const updateAcceptCheckbox = (offerIsAccepted: boolean, agreementText: string) => {
    setAcceptApplication({ offerIsAccepted, agreementText });
  };

  const updateModelState = (updatedModelState: ModelState) => {
    setModelState({ ...updatedModelState });
  };

  const addInsuredPerson = () => {
    dispatch({ type: "ADD_INSURED_PERSON" });
  };

  const removeInsuredPerson = (index: number) => {
    shouldRetrievePremiumsRef.current = true;
    dispatch({ type: "DELETE_INSURED_PERSON", payload: { index } });
  };

  const addCoverage = (effectiveInsured: string[]) => {
    dispatch({ type: "ADD_COVERAGE", payload: { effectiveInsured } });
  };

  const deleteCoverage = (index: number) => {
    shouldRetrievePremiumsRef.current = true;
    dispatch({ type: "DELETE_COVERAGE", payload: { index } });
  };

  const updatePaymentTerm = (paymentTerm: PaymentTerm) => {
    dispatch({ type: "UPDATE_PAYMENT_TERM", payload: { paymentTerm } });
  };

  const updateInsuredData = (index: number, insuredData: Partial<InsuredPerson>, shouldRetrievePremium?: boolean) => {
    if (shouldRetrievePremium) {
      shouldRetrievePremiumsRef.current = true;
    }
    dispatch({ type: "UPDATE_INSURED_DATA", payload: { index, data: insuredData } });
  };

  const updateInsuredDateOfBirth = (index: number, dateOfBirth: string) => {
    shouldRetrievePremiumsRef.current = true;
    dispatch({ type: "UPDATE_INSURED_DATA", payload: { index, data: { dateOfBirth } } });
  };

  const updateCoverageEffectiveInsured = (index: number, effectiveInsured: string[]) => {
    shouldRetrievePremiumsRef.current = true;
    dispatch({ type: "UPDATE_COVERAGE_EFFECTIVE_INSURED", payload: { index, effectiveInsured } });
  };

  const updateCoverageInsuredAmount = (index: number, insuredAmount: number | undefined, hasInsuredAmount: boolean) => {
    shouldRetrievePremiumsRef.current = true;
    dispatch({ type: "UPDATE_COVERAGE_INSURED_AMOUNT", payload: { index, insuredAmount, hasInsuredAmount } });
  };

  const updateCoverageHasInsuredAmount = (index: number) => {
    shouldRetrievePremiumsRef.current = true;
    dispatch({ type: "UPDATE_COVERAGE_HAS_INSURED_AMOUNT", payload: { index } });
  };

  const updateCoverageDuration = (index: number, duration: number | undefined, hasDuration: boolean) => {
    shouldRetrievePremiumsRef.current = true;
    dispatch({ type: "UPDATE_COVERAGE_DURATION", payload: { index, duration, hasDuration } });
  };

  const updateCoverageHasDuration = (index: number) => {
    shouldRetrievePremiumsRef.current = true;
    dispatch({ type: "UPDATE_COVERAGE_HAS_DURATION", payload: { index } });
  };

  const updateCoverageEffectiveDate = (index: number, effectiveDate?: string) => {
    shouldRetrievePremiumsRef.current = true;
    dispatch({ type: "UPDATE_COVERAGE_EFFECTIVE_DATE", payload: { index, effectiveDate } });
  };

  const updateCoverageInsuranceType = (index: number, insuranceType: CoverageInsuranceType) => {
    shouldRetrievePremiumsRef.current = true;
    dispatch({ type: "UPDATE_COVERAGE_INSURANCE_TYPE", payload: { index, insuranceType } });
  };

  const updateCoverageReductionRate = (index: number, reductionRate?: number) => {
    shouldRetrievePremiumsRef.current = true;
    dispatch({ type: "UPDATE_COVERAGE_REDUCTION_RATE", payload: { index, reductionRate } });
  };

  const updateInsuranceTaker = (insuranceTaker: string[]) => {
    dispatch({ type: "UPDATE_INSURANCE_TAKER", payload: { insuranceTaker } });
  };

  const updateCrosswiseInsured = (isCrosswiseInsured: YesOrNo) => {
    dispatch({ type: "UPDATE_CROSSWISE_INSURED", payload: { isCrosswiseInsured } });
  };

  const updateHasMortgage = (hasMortgage: boolean) => {
    dispatch({ type: "UPDATE_HAS_MORTGAGE", payload: { hasMortgage } });
  };

  const updateMortgageSupplier = (mortgageSupplierKey: string, mortgageSupplierLabel: string) => {
    dispatch({ type: "UPDATE_MORTGAGE_SUPPLIER", payload: { mortgageSupplierKey, mortgageSupplierLabel } });
  };

  const context: DlpContext = {
    acceptApplication,
    currentStep: currentStepRef.current,
    data,
    dictionary,
    goBack,
    goToStepFive,
    goToStepFour,
    goToStepThree,
    goToStepTwo,
    iban,
    isLoadingPremium,
    modelState,
    premium,
    setModelState: updateModelState,
    submitOffer,
    setters: {
      addInsuredPerson,
      removeInsuredPerson,
      addCoverage,
      deleteCoverage,
      updateAcceptCheckbox,
      updateCoverageEffectiveInsured,
      updateCoverageInsuredAmount,
      updateCoverageHasInsuredAmount,
      updateCoverageDuration,
      updateCoverageHasDuration,
      updateCoverageEffectiveDate,
      updateCoverageInsuranceType,
      updateCoverageReductionRate,
      updateIbanNumber,
      updateInsuredData,
      updateInsuredDateOfBirth,
      updateInsuranceTaker,
      updateCrosswiseInsured,
      updateHasMortgage,
      updateMortgageSupplier,
      updatePaymentTerm,
    },
  };

  const retrieveDictionary = () => {
    fetchDictionary()
      .then(({ phrases }) => {
        setDictionary(phrases);
      })
      .catch((e) => console.error(`Failed to fetch dictionary: ${e}`));
  };

  useDidMount(async () => {
    retrieveDictionary();
    if (applicationIdRef.current) {
      await retrieveApplication();
    }
  });

  useEffect(() => {
    if (shouldRetrievePremiumsRef.current) {
      retrievePremium();
    }
  }, [data, retrievePremium]);

  return (
    <DlpFlowContext.Provider value={context}>
      <LoadingOverlay loading={isLoadingExistingApplication}>{children}</LoadingOverlay>
    </DlpFlowContext.Provider>
  );
};
